import { useState, useCallback, useRef, useEffect } from 'react';
import {
  motion, AnimatePresence, MotionConfig, useScroll, useTransform, useMotionValueEvent,
} from 'framer-motion';
import { SectionContainer, AnimatedHeading, GlassCard } from '../components/ui';
import {
  ScrollProgress, Reveal, WordReveal, HeroBackdrop, useMotionLevel,
} from '../components/motion/ScrollMotion';

const STAGES = [
  {
    num: '01', id: 'binary', label: 'Binary Input', icon: '⬡', color: '#a0c4d8',
    desc: 'Upload a Windows PE (.exe, .dll) or Linux ELF executable. Other file types (including APK) are rejected and refunded; Mach-O analysis has not been validated. The file is saved to a temporary location, queued for analysis, and deleted afterwards.',
    details: ['Primary formats: PE and ELF', 'Non-executable files are rejected', 'Queued in an in-process job queue', 'Size limit set by the server (32 MB hosted)'],
    tool: 'FastAPI Upload',
  },
  {
    num: '02', id: 'extraction', label: 'Extraction', icon: '◈', color: '#00f0ff',
    desc: 'Multi-engine decompilation extracts functions, imports, exports, strings, and API call sequences. Radare2 is the primary engine; Ghidra is used only when installed on the server, and reports state when it is not available.',
    details: ['Function list with addresses and sizes', 'Import/export table parsing', 'String extraction (ASCII + Unicode)', 'API call sequence reconstruction'],
    tool: 'Radare2 (+ Ghidra if present)',
  },
  {
    num: '03', id: 'behaviour', label: 'Behaviour', icon: '◎', color: '#00c8ff',
    desc: 'Static behavioral signatures are applied to identify malicious patterns: network callbacks, process spawning, file system writes, registry modifications, and anti-analysis tricks.',
    details: ['Network communication patterns', 'Persistence mechanisms', 'Code injection techniques', 'Anti-VM / anti-debug heuristics'],
    tool: 'Custom Signature Engine',
  },
  {
    num: '04', id: 'capability', label: 'Capability', icon: '◆', color: '#009fcc',
    desc: 'CAPA rules (over 1,000) identify capabilities such as code injection or persistence. CAPA needs about 1 GB of memory, so it can be disabled on small hosts; reports then mark CAPA as Disabled. If CAPA is disabled, skipped or fails, a BENIGN verdict is then capped at 50% confidence (MALICIOUS and SUSPICIOUS verdicts are not capped).',
    details: ['1,000+ CAPA rules (when enabled)', 'Namespace-organized results', 'Attack phase classification', 'Platform-specific capability detection'],
    tool: 'CAPA (optional)',
  },
  {
    num: '05', id: 'mitre', label: 'MITRE ATT&CK', icon: '◉', color: '#ff8c42',
    desc: 'Detected capabilities are mapped to candidate MITRE ATT&CK technique IDs, shown for analyst review rather than as confirmed findings.',
    details: ['Candidate technique IDs', 'Technique + sub-technique IDs', 'Tactic phase labeling', 'Marked as candidates'],
    tool: 'MITRE ATT&CK',
  },
  {
    num: '06', id: 'risk', label: 'Risk Score', icon: '▲', color: '#ffb347',
    desc: 'Rule-based heuristics produce a normalized 0–100 risk score, weighing capability severity, technique count, and behavioral indicators.',
    details: ['0–100 normalized risk scale', 'Rule-based heuristics', 'Weighted capability scoring', 'Behavioral indicators'],
    tool: 'Heuristic Scoring',
  },
  {
    num: '07', id: 'cvss', label: 'CVSS', icon: '◐', color: '#ff6b6b',
    desc: 'A CVSS-style 0–10 severity estimate is derived from the detected behaviors. It is a heuristic: not an official CVSS v3.1 vector calculation and not derived from the NVD.',
    details: ['Severity estimate 0.0–10.0', 'Derived from detected behaviors', 'Heuristic, not official CVSS', 'Not NVD-derived'],
    tool: 'Heuristic Severity',
  },
  {
    num: '08', id: 'verdict', label: 'Verdict', icon: '★', color: '#ff3c6e',
    desc: 'The analysis pipeline concludes with a final verdict: MALICIOUS, SUSPICIOUS, or BENIGN. Confidence percentage (BENIGN is capped at 50% when CAPA is disabled, skipped or fails), malware category, and a human-readable summary are generated.',
    details: ['MALICIOUS / SUSPICIOUS / BENIGN', 'Confidence percentage 0–100%', 'Threat category classification', 'Natural language summary'],
    tool: 'Verdict Engine',
  },
];

const CYAN = '#00f0ff';

/* ── Pinned HUD (desktop only): stays under the navbar while the flow scrolls ── */
function StageRail({ reached }) {
  const cur = reached >= 0 ? STAGES[reached] : null;
  const mono = { fontFamily: 'Space Mono', fontSize: 9, letterSpacing: 2, color: '#6b8fa8', textTransform: 'uppercase', whiteSpace: 'nowrap' };
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'sticky', top: 76, zIndex: 5, maxWidth: 800, margin: '0 auto 1.5rem',
        display: 'flex', alignItems: 'center', gap: 14, padding: '9px 14px',
        background: 'rgba(2,8,16,0.94)', border: '1px solid rgba(0,240,255,0.14)', borderRadius: 6,
      }}
    >
      <span style={mono}>{cur ? `Stage ${cur.num} / ${String(STAGES.length).padStart(2, '0')}` : 'Pipeline'}</span>
      <div style={{ flex: 1, display: 'flex', gap: 4 }}>
        {STAGES.map((s, i) => (
          <div key={s.id} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: i <= reached ? s.color : 'rgba(0,240,255,0.12)',
            boxShadow: i === reached ? `0 0 8px ${s.color}` : 'none',
            transition: 'background 0.3s, box-shadow 0.3s',
          }} />
        ))}
      </div>
      <span style={{ ...mono, color: cur ? cur.color : '#6b8fa8', minWidth: 96, textAlign: 'right' }}>
        {cur ? cur.label : '—'}
      </span>
    </div>
  );
}

/* ── Scroll-driven pipeline flow ─────────────────────────────────────────────────
 * A cyan line fills as the user scrolls; each stage activates when the line
 * reaches it. "Reached" state is independent from `activeStage` (the selected
 * tab), so the existing click-to-jump behaviour is unchanged. With reduced
 * motion every stage is simply shown in its reached state, in order. */
function PipelineFlow({ activeStage, onSelect }) {
  const level = useMotionLevel();
  const animated = level !== 'none';
  const full = level === 'full';
  const wrapRef = useRef(null);
  const marks = useRef([]);
  const [reached, setReached] = useState(-1);

  // The line tip sits at 65% of the viewport height.
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ['start 65%', 'end 65%'] });
  const tipY = useTransform(scrollYProgress, (v) => `${v * 100}%`);
  const tipOpacity = useTransform(scrollYProgress, [0, 0.01, 0.99, 1], [0, 1, 1, 0]);
  const packetClip = useTransform(scrollYProgress, (v) => `inset(0px -12px ${(1 - v) * 100}% -12px)`);

  const indexFor = useCallback((p) => {
    let idx = -1;
    for (let i = 0; i < marks.current.length; i += 1) if (p >= marks.current[i]) idx = i;
    return idx;
  }, []);

  // Measure each row's centre (as a fraction of the flow height) so the line
  // and the stage activation stay in sync at any viewport width.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const measure = () => {
      const h = wrap.offsetHeight || 1;
      marks.current = Array.from(wrap.querySelectorAll('[data-flow-row]'))
        .map((el) => (el.offsetTop + el.offsetHeight / 2) / h);
      const next = indexFor(scrollYProgress.get());
      setReached((prev) => (prev === next ? prev : next));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(wrap);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [indexFor, scrollYProgress]);

  // Only re-renders when the active stage index actually changes (≤ 8 times).
  useMotionValueEvent(scrollYProgress, 'change', (p) => {
    if (!animated) return;
    const next = indexFor(p);
    setReached((prev) => (prev === next ? prev : next));
  });

  const eff = animated ? reached : STAGES.length - 1;

  return (
    <div>
      {full && <StageRail reached={eff} />}

      <div ref={wrapRef} style={{ position: 'relative', maxWidth: 800, margin: '0 auto' }}>
        {/* Centre vertical rule (original) */}
        <div aria-hidden="true" className="arise-flow-rule" style={{
          position: 'absolute', left: '50%', top: 0, bottom: 0,
          width: 1, background: 'linear-gradient(180deg, transparent, rgba(0,240,255,0.2), transparent)',
          transform: 'translateX(-50%)',
        }} />

        {/* Scroll-driven cyan progress line */}
        <div aria-hidden="true" className="arise-flow-track" style={{
          position: 'absolute', left: '50%', marginLeft: -1, top: 0, bottom: 0, width: 2, pointerEvents: 'none',
        }}>
          <motion.div style={{
            position: 'absolute', inset: 0, transformOrigin: 'top',
            scaleY: animated ? scrollYProgress : 1,
            background: `linear-gradient(180deg, rgba(0,240,255,0.25), ${CYAN})`,
            boxShadow: '0 0 8px rgba(0,240,255,0.45)',
          }} />
          {full && (
            <motion.div style={{ position: 'absolute', inset: 0, clipPath: packetClip }}>
              {[0, 2.6].map((d) => (
                <motion.div
                  key={d}
                  style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '100%' }}
                  animate={{ y: ['0%', '100%'], opacity: [0, 1, 1, 0] }}
                  transition={{ duration: 5.2, delay: d, repeat: Infinity, ease: 'linear', times: [0, 0.08, 0.92, 1] }}
                >
                  <span style={{
                    position: 'absolute', left: '50%', top: 0, width: 5, height: 5,
                    marginLeft: -2.5, borderRadius: '50%', background: '#e0f4ff',
                    boxShadow: `0 0 8px ${CYAN}`,
                  }} />
                </motion.div>
              ))}
            </motion.div>
          )}
          {animated && (
            <motion.div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '100%', y: tipY, opacity: tipOpacity }}>
              <span style={{
                position: 'absolute', left: '50%', top: 0, width: 9, height: 9,
                marginLeft: -4.5, marginTop: -4.5, borderRadius: '50%', background: CYAN,
                boxShadow: `0 0 14px ${CYAN}, 0 0 28px rgba(0,240,255,0.4)`,
              }} />
            </motion.div>
          )}
        </div>

        {STAGES.map((stage, i) => {
          const isReached = i <= eff;
          const isCurrent = animated && i === eff;
          const isSel = activeStage === i;
          const left = i % 2 === 0;
          return (
            <motion.div
              key={stage.id}
              data-flow-row
              className="arise-flow-row"
              initial={animated ? { opacity: 0, x: full ? (left ? -36 : 36) : 0, y: full ? 0 : 20 } : false}
              whileInView={animated ? { opacity: 1, x: 0, y: 0 } : undefined}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              style={{
                display: 'flex',
                justifyContent: left ? 'flex-end' : 'flex-start',
                marginBottom: '1.25rem',
                paddingLeft:  left ? 0 : '52%',
                paddingRight: left ? '52%' : 0,
                position: 'relative',
              }}
            >
              <button
                className="arise-flow-btn"
                onClick={() => onSelect(i)}
                aria-label={`Jump to stage ${stage.num}: ${stage.label}`}
                style={{
                  padding: '0.85rem 1.1rem', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${isSel ? stage.color : isReached ? `${stage.color}55` : 'rgba(0,240,255,0.1)'}`,
                  background: isSel ? `${stage.color}10` : isReached ? `${stage.color}06` : 'rgba(6,18,36,0.8)',
                  transition: 'all 0.2s', maxWidth: 260, textAlign: 'left',
                  boxShadow: isSel
                    ? `0 0 18px ${stage.color}18`
                    : isCurrent ? `0 0 16px ${stage.color}20` : 'none',
                  width: '100%',
                  opacity: isReached ? 1 : 0.62,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span aria-hidden="true" style={{
                    fontSize: 16, color: stage.color,
                    textShadow: isReached && animated ? `0 0 10px ${stage.color}` : 'none',
                    transition: 'text-shadow 0.3s',
                  }}>{stage.icon}</span>
                  <div>
                    <div style={{ fontFamily: 'Space Mono', fontSize: 8, color: '#6b8fa8', letterSpacing: 1 }}>
                      STAGE {stage.num}
                    </div>
                    <div style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 15, color: isSel ? stage.color : '#e0f4ff' }}>
                      {stage.label}
                    </div>
                  </div>
                </div>
              </button>

              {/* Centre node: lights up when the progress line reaches it */}
              <motion.div
                aria-hidden="true"
                className="arise-flow-dot"
                animate={{ scale: isCurrent ? 1.4 : 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                style={{
                  position: 'absolute', left: '50%', top: '50%', x: '-50%', y: '-50%',
                  width: 9, height: 9, borderRadius: '50%',
                  background: isReached ? stage.color : 'rgba(0,240,255,0.18)',
                  boxShadow: isReached ? `0 0 10px ${stage.color}` : 'none',
                  transition: 'background 0.3s, box-shadow 0.3s',
                  flexShrink: 0,
                }}
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function HowItWorksContent() {
  const [activeStage, setActiveStage] = useState(0);
  const tabListRef = useRef(null);
  const level = useMotionLevel();
  const animated = level !== 'none';

  /* Arrow key navigation on the tab row */
  const handleTabKeyDown = useCallback((e, i) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (i + 1) % STAGES.length;
      setActiveStage(next);
      tabListRef.current?.querySelectorAll('[role="tab"]')[next]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (i - 1 + STAGES.length) % STAGES.length;
      setActiveStage(prev);
      tabListRef.current?.querySelectorAll('[role="tab"]')[prev]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveStage(0);
      tabListRef.current?.querySelectorAll('[role="tab"]')[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = STAGES.length - 1;
      setActiveStage(last);
      tabListRef.current?.querySelectorAll('[role="tab"]')[last]?.focus();
    }
  }, []);

  const jumpToStage = useCallback((i) => {
    setActiveStage(i);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const s = STAGES[activeStage];

  return (
    <div className="page-wrapper" style={{ paddingTop: 64 }}>
      <ScrollProgress />
      <SectionContainer as="section">
        {/* Hero: page-local pipeline backdrop (global background untouched) */}
        <div style={{ position: 'relative', overflowX: 'clip' }}>
          <HeroBackdrop variant="pipeline" />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <AnimatedHeading
              center
              subtitle="Every binary goes through an 8-stage pipeline that combines open-source tooling, heuristic scoring, and the MITRE ATT&CK framework."
              level={1}
            >
              <WordReveal text="The " style={{ color: '#e0f4ff' }} />
              <WordReveal text="ARISE Pipeline" className="neon-text" delay={0.3} />
            </AnimatedHeading>
          </div>
        </div>

        {/* Stage selector — accessible tab list */}
        <Reveal y={16} blur={0} delay={0.3}>
          <div
            role="tablist"
            aria-label="Pipeline stages"
            ref={tabListRef}
            style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '2.5rem' }}
          >
            {STAGES.map((st, i) => (
              <motion.button
                key={st.id}
                role="tab"
                id={`tab-${st.id}`}
                aria-selected={activeStage === i}
                aria-controls={`panel-${st.id}`}
                tabIndex={activeStage === i ? 0 : -1}
                onClick={() => setActiveStage(i)}
                onKeyDown={(e) => handleTabKeyDown(e, i)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  background: activeStage === i ? `${st.color}15` : 'rgba(6,18,36,0.6)',
                  border: `1px solid ${activeStage === i ? st.color : 'rgba(0,240,255,0.12)'}`,
                  color: activeStage === i ? st.color : '#6b8fa8',
                  fontFamily: 'Space Mono', fontSize: 9,
                  letterSpacing: 1, textTransform: 'uppercase',
                  padding: '5px 12px', borderRadius: 4,
                  cursor: 'pointer', transition: 'all 0.2s',
                  boxShadow: activeStage === i ? `0 0 14px ${st.color}28` : 'none',
                }}
              >
                {st.num} {st.label}
              </motion.button>
            ))}
          </div>
        </Reveal>

        {/* Active stage detail panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStage}
            role="tabpanel"
            id={`panel-${s.id}`}
            aria-labelledby={`tab-${s.id}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
          >
            <GlassCard style={{ padding: 'clamp(1.5rem,4vw,2.5rem)' }} glow color={s.color}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
                gap: 'clamp(1.5rem,4vw,3rem)',
                alignItems: 'start',
              }}
                className="arise-hiw-detail"
              >
                {/* Left: description */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <motion.div
                      initial={animated ? { scale: 0.6, rotate: -18, opacity: 0 } : false}
                      animate={{ scale: 1, rotate: 0, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.1 }}
                      style={{
                        width: 60, height: 60, borderRadius: 12, flexShrink: 0,
                        background: `${s.color}15`, border: `1px solid ${s.color}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 26, color: s.color,
                        boxShadow: `0 0 22px ${s.color}28`,
                      }}
                      aria-hidden="true"
                    >
                      {s.icon}
                    </motion.div>
                    <div>
                      <div style={{ fontFamily: 'Space Mono', fontSize: 9, color: '#6b8fa8', letterSpacing: 2, textTransform: 'uppercase' }}>
                        Stage {s.num}
                      </div>
                      <h2 style={{ fontFamily: 'Orbitron', fontSize: 'clamp(1.1rem,2.5vw,1.5rem)', fontWeight: 700, color: s.color, letterSpacing: '0.04em', textShadow: `0 0 18px ${s.color}38` }}>
                        {s.label}
                      </h2>
                    </div>
                  </div>
                  <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#a0c4d8', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                    {s.desc}
                  </p>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    border: `1px solid ${s.color}30`, borderRadius: 4,
                    padding: '5px 12px', background: `${s.color}08`,
                  }}>
                    <span style={{ fontFamily: 'Space Mono', fontSize: 9, color: s.color, letterSpacing: 1 }}>
                      ENGINE: {s.tool}
                    </span>
                  </div>
                </div>

                {/* Right: extraction list */}
                <div>
                  <div style={{ fontFamily: 'Space Mono', fontSize: 9, color: '#6b8fa8', letterSpacing: 2, textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                    What gets extracted
                  </div>
                  <ul style={{ listStyle: 'none' }}>
                    {s.details.map((d, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        whileHover={level === 'full' ? { x: 4 } : undefined}
                        transition={{ delay: i * 0.07 }}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: '0.65rem 0.9rem', borderRadius: 6,
                          background: 'rgba(0,240,255,0.03)',
                          border: '1px solid rgba(0,240,255,0.07)',
                          marginBottom: '0.4rem',
                        }}
                      >
                        <span aria-hidden="true" style={{ color: s.color, fontSize: 11, marginTop: 2, flexShrink: 0 }}>▸</span>
                        <span style={{ fontFamily: 'Inter', fontSize: 13, color: '#a0c4d8', lineHeight: 1.5 }}>{d}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </AnimatePresence>

        {/* Dot pagination */}
        <div
          role="group"
          aria-label="Stage pagination"
          style={{ display: 'flex', justifyContent: 'center', gap: '0.45rem', marginTop: '1.5rem' }}
        >
          {STAGES.map((st, i) => (
            <button
              key={i}
              onClick={() => setActiveStage(i)}
              aria-label={`Go to stage ${st.num}: ${st.label}`}
              aria-current={i === activeStage ? 'true' : undefined}
              style={{
                width: i === activeStage ? 30 : 8, height: 8,
                borderRadius: 4, border: 'none',
                background: i === activeStage ? st.color : 'rgba(0,240,255,0.15)',
                cursor: 'pointer', transition: 'all 0.3s', padding: 0,
              }}
            />
          ))}
        </div>

        {/* Full pipeline timeline */}
        <div style={{ marginTop: '4rem' }}>
          <AnimatedHeading center level={2}>
            <WordReveal inView text="Full " style={{ color: '#e0f4ff' }} />
            <WordReveal inView text="Pipeline Flow" className="neon-text" delay={0.3} />
          </AnimatedHeading>

          <PipelineFlow activeStage={activeStage} onSelect={jumpToStage} />
        </div>
      </SectionContainer>

      <style>{`
        @media (max-width: 680px) {
          .arise-hiw-detail { grid-template-columns: 1fr !important; }
          /* Flow: single column with the line on the left (the 52% zig-zag is too tight on phones) */
          .arise-flow-row  { justify-content: flex-start !important; padding-left: 2.4rem !important; padding-right: 0 !important; }
          .arise-flow-btn  { max-width: none !important; }
          .arise-flow-rule { left: 11px !important; }
          .arise-flow-track{ left: 11px !important; }
          .arise-flow-dot  { left: 11px !important; }
        }
      `}</style>
    </div>
  );
}

/* reducedMotion="user": the tab-panel slide, list-item slide and tab hover/tap
 * scale now honour the OS reduced-motion setting (they previously ignored it). */
export default function HowItWorksPage() {
  return (
    <MotionConfig reducedMotion="user">
      <HowItWorksContent />
    </MotionConfig>
  );
}
